import type { FaucetState } from '@/net/api';
import { formatCountdown } from '@/ui/countdown';

/**
 * The faucet's words that are not markup (Blockari's `screens/faucet-copy.ts` + `shell/ProfileFaucet.tsx` @ 17aabed):
 * the countdown, the entry's label, which state the screen is in, and what each refusal says. Pure (no DOM), so
 * the rules are tested in node; `faucet.ts` draws them.
 *
 * Every error code the server can answer has a sentence here. The server writes its own too (`message` on the
 * body, services in faucet.ts / faucet-verify.ts); the three that carry something only it knows (`not-eligible`'s wait,
 * `handle-taken`'s handle, `missing-tags`' list of what is missing) use the server's, everything else is this table, so a machine code never reaches a
 * player and a changed server sentence cannot reword the screen.
 */

/** "4d 21h 14m 09s": the one format of every faucet wait (countdown.ts). */
export { formatCountdown };

/**
 * The post link in whatever was pasted. A share sheet or a copy button often hands over "<link>\n<some words>" (or a
 * link inside a sentence): the first http(s) URL is the link, with the punctuation a sentence puts after it dropped.
 * Text with no http(s) URL in it ("x.com/you/status/1") is kept as it is, minus any whitespace (a link has none), and
 * the server decides whether it is a post link. Never a request: this only tidies what the player pasted.
 */
export function linkFrom(text: string): string {
  const url = /https?:\/\/\S+/i.exec(text)?.[0];
  if (url) return url.replace(/[.,;:!?)\]}"'>]+$/, '');
  return text.replace(/\s+/g, '');
}

/** Which of the screen's four bodies the state shows. */
export type FaucetView = 'guest' | 'cooldown' | 'no-game' | 'ready';

/**
 * The server reports `nextClaimAt` only while a wait is running, so a state that carries one IS a cooldown, whatever
 * this clock says: a wait that has run out stays on screen, counting 00s and asking again every 5 s (countdown.ts),
 * until the server says the claim is open. The page never opens a claim the server has not.
 */
export function viewOf(state: FaucetState): FaucetView {
  if (state.guest) return 'guest';
  if (state.nextClaimAt !== null) return 'cooldown';
  return state.finishedGame ? 'ready' : 'no-game';
}

/**
 * The entry's label (home and your own profile): "Free tokens · +100" when a claim would be accepted, "Free tokens in
 * 4d 21h 14m 09s" while the wait runs (the buttons draw it ticking, see countdown.ts; this is the same text as a string), plain "Free tokens" for a guest, a player with no finished game, or no answer yet.
 */
export function entryLabel(state: FaucetState | null, now = Date.now()): string {
  if (state && !state.guest && state.nextClaimAt !== null) return `Free tokens in ${formatCountdown(Date.parse(state.nextClaimAt) - now)}`;
  return state?.canClaim ? `Free tokens · +${state.amount}` : 'Free tokens';
}

/** True when the entry is the quiet button (waiting), false for the warm one (anything else). */
export function entryIsWaiting(state: FaucetState | null): boolean {
  return state !== null && !state.guest && state.nextClaimAt !== null;
}

/** Every code the faucet's routes can answer, and the sentence for each (`not-eligible` and `handle-taken` take the server's). */
export const ERROR_COPY: Readonly<Record<string, string>> = {
  'not-signed-in': 'Sign in to claim your free tokens.',
  'guest-not-allowed': 'Create an account to claim your free tokens.',
  'bad-link': 'Paste the link to your post on X (like x.com/you/status/123…).',
  'not-eligible': 'You have already claimed this week. Come back when the wait is over.',
  'no-finished-game': 'Finish a challenge game first, then come back to claim your tokens.',
  'post-used': 'That post has already been used to claim.',
  'handle-taken': 'That X account is already linked to another 21 Wild account.',
  'post-too-old': 'That post is older than your link. Share again from this page and paste the new post.',
  'post-not-found': "We couldn't find that post. Check the link, and that your account isn't private.",
  'missing-tags': 'The post needs #21Wild and @realchaingames. Share from this page so the text is filled in.',
  'wrong-code': "That post carries someone else's link. Post your own, from this page.",
  'missing-code': "That post doesn't carry your personal link. Share from this page so it is included.",
  'verify-unavailable': "We couldn't reach X to check your post. Try again in a minute.",
  'rate-limited': 'Too many tries. Wait a little and try again.',
  'faucet-off': 'Free tokens are not available right now.',
  'no-user': 'We could not find your account. Sign in again.',
  'not-found': 'That page is not available.',
  // The card upload: never shown (the share carries on with the generic card), but a code is a code.
  'bad-image': 'The card could not be saved.',
  'card-too-large': 'The card could not be saved.',
};

/** Codes whose sentence carries something only the server knows (a wait, a handle): its words are used as they are. */
const SERVER_WORDS = new Set(['not-eligible', 'handle-taken', 'missing-tags']);

/** Codes after which the screen should re-read its state: the cooldown or the eligibility moved under it. */
export const STATE_CHANGED_CODES: readonly string[] = ['not-eligible', 'no-finished-game', 'guest-not-allowed'];

/** What a failed call says: a sentence for the player, never a code. */
export function faucetError(failure: { error?: string; status?: number; body?: unknown }): string {
  const code = failure.error ?? '';
  const message = (failure.body as { message?: unknown } | null | undefined)?.message;
  if (SERVER_WORDS.has(code) && typeof message === 'string' && message !== '') return message;
  const known = ERROR_COPY[code];
  if (known) return known;
  // No answer from the server at all: a dropped connection, a timeout.
  if (failure.status === undefined) return "We couldn't reach the server. Check your connection and try again.";
  if (failure.status === 429) return ERROR_COPY['rate-limited']!;
  return 'Something went wrong. Try again.';
}
